import { useMemo } from "react";
import { OrderBook } from "@/components/features/trade/OrderBook/types";

const PRECISION_FALLBACK = 0.01;

// Helper function to find the smallest precision from orderbook data
const findSmallestPrecision = (orderBook: OrderBook): number => {
  const allPrices = [...orderBook.bids.map(([price]) => price), ...orderBook.asks.map(([price]) => price)];

  if (allPrices.length === 0) return PRECISION_FALLBACK;

  // Find the maximum number of decimal places
  let maxDecimalPlaces = 0;

  for (const price of allPrices) {
    const priceStr = price.toString();
    const decimalIndex = priceStr.indexOf(".");

    if (decimalIndex !== -1) {
      const decimalPlaces = priceStr.length - decimalIndex - 1;
      maxDecimalPlaces = Math.max(maxDecimalPlaces, decimalPlaces);
    }
  }

  // If no decimal places found, default to 2 decimal places (0.01)
  if (maxDecimalPlaces === 0) {
    maxDecimalPlaces = 2;
  }

  // Create clean precision using string-based approach to avoid floating-point errors
  const precisionStr = "0." + "0".repeat(maxDecimalPlaces - 1) + "1";
  return parseFloat(precisionStr);
};

export const usePrecisionList = (pairSymbol?: string, orderBook?: OrderBook): number[] => {
  const precisions = useMemo(() => {
    if (!pairSymbol || !orderBook) return [];

    const basePrecision = findSmallestPrecision(orderBook);

    // Helper to create clean precision values
    const createCleanPrecision = (value: number): number => {
      // Round to avoid floating-point precision issues
      const rounded = Math.round(value * 1e10) / 1e10;
      return rounded;
    };

    const x10 = createCleanPrecision(basePrecision * 10);
    const x100 = createCleanPrecision(basePrecision * 100);
    const x1000 = createCleanPrecision(basePrecision * 1000);

    return [basePrecision, x10, x100, x1000];
  }, [orderBook, pairSymbol]);

  return precisions;
};
